import React, { useState } from 'react';
import { View } from 'react-native';
import { useQuery } from '@tanstack/react-query';
import { fmtDate, fmtTime, inr, P, periodLabel, type Payslip, type PayrollRunStatus } from '@hrms/shared';
import { useAuth } from '../../../auth/AuthProvider';
import { AutoGrid } from '../components/common';
import { api } from '../../../lib/api';
import { openFile } from '../../../lib/files';
import { useMut } from '../../../lib/query';
import { useGo, useParams } from '../../../navigation/nav';
import { Alert, Badge, Button, Card, ConfirmDialog, DataTable, ErrorState, Loading, PageHeader, Row, Screen, Stack, StatCard, StatusBadge, Stepper, Switch, Text, useTheme } from '../../../ui';
import { PayslipSheet } from '../components/Breakdown';
import { PersonCell } from '../components/EmployeePicker';
import { OUTPUTS, type OutputKind } from '../components/outputs';

const STEPS = ['Inputs', 'Process', 'Preview', 'Lock', 'Mark paid'];
const stepOf = (s: PayrollRunStatus) => ({ draft: 1, processing: 1, processed: 2, locked: 4, paid: 5 })[s];

export function PayrollRunScreen() {
  const id = Number(useParams<{ id: string }>().id);
  const go = useGo();
  const { colors } = useTheme();
  const { can } = useAuth();
  const manage = can(P.PAYROLL_MANAGE);
  const run = useQuery({
    queryKey: ['payroll', 'run', id],
    queryFn: () => api.payroll.run(id),
    enabled: !!id,
    refetchInterval: (q) => (q.state.data?.status === 'processing' ? 3000 : false),
  });
  const [varOnly, setVarOnly] = useState(false);
  const [page, setPage] = useState(1);
  const slips = useQuery({
    queryKey: ['payroll', 'run', id, 'payslips', varOnly, page],
    queryFn: () => api.payroll.runPayslips(id, { variance_only: varOnly ? 1 : 0, page }),
    enabled: !!run.data && run.data.status !== 'draft' && run.data.status !== 'processing',
  });
  const [sel, setSel] = useState<Payslip | null>(null);
  const [confirm, setConfirm] = useState<'lock' | 'paid' | 'reverse' | null>(null);
  const inv = { invalidate: [['payroll']] };
  const process = useMut(() => api.payroll.process(id), { ...inv, success: 'Payroll processed' });
  const lock = useMut(() => api.payroll.lock(id), { ...inv, success: 'Payroll locked — payslips published', onSuccess: () => setConfirm(null) });
  const paid = useMut(() => api.payroll.markPaid(id), { ...inv, success: 'Marked as paid', onSuccess: () => setConfirm(null) });
  const reverse = useMut(() => api.payroll.reverse(id), { ...inv, success: 'Run reversed to draft', onSuccess: () => setConfirm(null) });

  if (run.isLoading) return <Loading />;
  if (run.isError || !run.data)
    return (
      <Screen>
        <PageHeader title="Payroll run" />
        <ErrorState error={run.error} onRetry={() => run.refetch()} />
      </Screen>
    );
  const r = run.data;
  const step = stepOf(r.status);
  const out = (k: OutputKind) => void openFile(api.payroll.outputPath(id, k));
  const lockedOrPaid = r.status === 'locked' || r.status === 'paid';

  return (
    <Screen refreshing={run.isRefetching} onRefresh={() => run.refetch()} testID="payroll-run">
      <PageHeader
        title={`Payroll · ${periodLabel(r.period)}`}
        subtitle={r.processed_at ? `Processed ${fmtDate(r.processed_at)} ${fmtTime(r.processed_at)}` : undefined}
        actions={<StatusBadge status={r.status} />}
      />
      <Card>
        <Stepper steps={STEPS} current={step} onStepPress={(i) => (i === 0 ? go('PayrollInputs', { period: r.period }) : undefined)} />
      </Card>

      {manage && (
        <Card testID="run-actions">
          <Stack gap={12}>
            <View style={{ gap: 4 }}>
              {r.status === 'draft' && (
                <>
                  <Text weight="semibold">Ready to process.</Text>
                  <Text variant="muted">Computes gross, statutory deductions (PF, ESI, PT, LWF), TDS and net pay for all eligible employees. Review inputs first.</Text>
                </>
              )}
              {r.status === 'processing' && (
                <>
                  <Text weight="semibold">Processing…</Text>
                  <Text variant="muted">This page refreshes automatically.</Text>
                </>
              )}
              {r.status === 'processed' && (
                <>
                  <Text weight="semibold">Review payslips below.</Text>
                  <Text variant="muted">Variance flags highlight employees whose net pay moved significantly vs last month. Re-process after fixing inputs.</Text>
                </>
              )}
              {r.status === 'locked' && (
                <>
                  <Text weight="semibold">Locked.</Text>
                  <Text variant="muted">Download the bank file, release salaries, then mark as paid.</Text>
                </>
              )}
              {r.status === 'paid' && (
                <>
                  <Text weight="semibold">Paid {fmtDate(r.paid_at)}.</Text>
                  <Text variant="muted">Statutory challans are available below.</Text>
                </>
              )}
            </View>
            <Row>
              {(r.status === 'draft' || r.status === 'processed') && <Button icon="grid" title="Review inputs" onPress={() => go('PayrollInputs', { period: r.period })} />}
              {(r.status === 'draft' || r.status === 'processed') && (
                <Button variant={r.status === 'draft' ? 'primary' : 'secondary'} icon="play" title={r.status === 'draft' ? 'Process payroll' : 'Re-process'} loading={process.isPending} onPress={() => process.mutate(undefined)} testID="process-run" />
              )}
              {r.status === 'processed' && <Button variant="primary" icon="lock" title="Lock payroll" onPress={() => setConfirm('lock')} testID="lock-run" />}
              {r.status === 'locked' && <Button variant="success" icon="check" title="Mark as paid" onPress={() => setConfirm('paid')} testID="paid-run" />}
              {(r.status === 'processed' || r.status === 'locked') && <Button variant="ghost" icon="refresh" title="Reverse" onPress={() => setConfirm('reverse')} />}
            </Row>
          </Stack>
        </Card>
      )}

      <AutoGrid min={160}>
        <StatCard label="Employees" value={r.employee_count} icon="users" />
        <StatCard label="Gross" value={inr(r.total_gross)} icon="wallet" tone="info" />
        <StatCard label="Deductions" value={inr(r.total_deductions)} icon="receipt" tone="danger" />
        <StatCard label="Net pay" value={inr(r.total_net)} icon="checkCircle" tone="success" />
        <StatCard label="Employer cost" value={inr(r.total_employer_cost)} icon="building" tone="neutral" />
        <StatCard label="Variance flags" value={r.variance_flags} icon="alert" tone={r.variance_flags ? 'warning' : 'neutral'} onPress={r.variance_flags ? () => { setVarOnly(true); setPage(1); } : undefined} />
      </AutoGrid>

      {(r.status === 'processed' || lockedOrPaid) && (
        <Card title="Outputs" subtitle={r.status === 'processed' ? 'Bank and statutory files are available after locking.' : undefined}>
          <Row>
            {OUTPUTS.map((o) => (
              <Button key={o.kind} icon="download" title={o.title} disabled={o.needsLock && !lockedOrPaid} onPress={() => out(o.kind)} testID={`out-${o.kind}`} />
            ))}
          </Row>
        </Card>
      )}

      {r.status !== 'draft' && r.status !== 'processing' && (
        <Stack gap={10}>
          <Row justify="space-between">
            <Text variant="title">Payslips</Text>
            <View style={{ minWidth: 220 }}>
              <Switch
                label="Variance flags only"
                value={varOnly}
                onChange={(v) => {
                  setVarOnly(v);
                  setPage(1);
                }}
              />
            </View>
          </Row>
          <DataTable
            rows={slips.data}
            loading={slips.isLoading}
            onPageChange={setPage}
            onRowPress={setSel}
            emptyTitle={varOnly ? 'No variance flags' : 'No payslips'}
            testID="run-payslips"
            columns={[
              { key: 'employee', title: 'Employee', width: 200, flex: 2, render: (p) => <PersonCell emp={p.employee} fallback={p.employee_id} />, sortValue: (p) => p.employee?.full_name },
              { key: 'payable_days', title: 'Paid days', width: 90, align: 'right', render: (p) => `${p.payable_days}/${p.days_in_month}`, sortValue: (p) => p.payable_days },
              { key: 'lop_days', title: 'LOP', width: 70, align: 'right', render: (p) => (p.lop_days ? <View><Badge tone="danger">{p.lop_days}</Badge></View> : '—'), sortValue: (p) => p.lop_days },
              { key: 'gross', title: 'Gross', width: 110, align: 'right', render: (p) => inr(p.gross), sortValue: (p) => p.gross },
              { key: 'total_deductions', title: 'Deductions', width: 110, align: 'right', render: (p) => inr(p.total_deductions), sortValue: (p) => p.total_deductions },
              { key: 'tds', title: 'TDS', width: 90, align: 'right', render: (p) => inr(p.tds), sortValue: (p) => p.tds },
              { key: 'net_pay', title: 'Net', width: 110, align: 'right', render: (p) => <Text weight="semibold">{inr(p.net_pay)}</Text>, sortValue: (p) => p.net_pay },
              {
                key: 'variance_pct',
                title: 'Variance',
                width: 100,
                align: 'right',
                sortValue: (p) => p.variance_pct,
                render: (p) =>
                  p.variance_pct == null ? (
                    <View><Badge tone="info">New</Badge></View>
                  ) : (
                    <Text weight={p.variance_flag ? 'semibold' : 'regular'} color={p.variance_flag ? colors.warning : undefined}>
                      {`${p.variance_pct > 0 ? '+' : ''}${p.variance_pct.toFixed(1)}%${p.variance_flag ? ' ⚑' : ''}`}
                    </Text>
                  ),
              },
            ]}
          />
        </Stack>
      )}

      <PayslipSheet id={sel?.id ?? null} title={sel?.employee?.full_name} onClose={() => setSel(null)} />
      <ConfirmDialog
        open={confirm === 'lock'}
        onClose={() => setConfirm(null)}
        onConfirm={() => lock.mutate(undefined)}
        loading={lock.isPending}
        title="Lock payroll?"
        message={`Payslips will be published to employees and inputs for ${periodLabel(r.period)} become read-only.`}
        confirmLabel="Lock"
      />
      <ConfirmDialog open={confirm === 'paid'} onClose={() => setConfirm(null)} onConfirm={() => paid.mutate(undefined)} loading={paid.isPending} title="Mark as paid?" message="Confirm salaries have been credited to employee bank accounts." confirmLabel="Mark paid" />
      <ConfirmDialog
        open={confirm === 'reverse'}
        onClose={() => setConfirm(null)}
        onConfirm={() => reverse.mutate(undefined)}
        loading={reverse.isPending}
        title="Reverse payroll run?"
        message="All payslips for this run will be discarded and the run returns to draft."
        danger
        confirmLabel="Reverse"
      />
      {r.variance_flags > 0 && r.status === 'processed' && <Alert tone="warning">{`${r.variance_flags} employee(s) have net pay variance above the threshold. Review before locking.`}</Alert>}
    </Screen>
  );
}

